/* Payslip: A4/Letter/half-sheet layout and thermal-roll receipt (print.js payslipBody/payslipRoll). */
import type { ReactNode } from "react";
import type { Employee, GosiSettings, Payslip } from "../../../data/types";
import { hDate, MONTHS_AR, MONTHS_LONG_EN } from "../../../lib/format";
import { maskIban, n2, Qr, VerifyBlock, empName, type PCtx } from "./kit";
import { curCode, otPct } from "../../../lib/country";

export const payslipRef = (e: Employee, period: string) => "PS-" + period.replace("-", "") + "-" + e.code.replace(/\D/g, "").slice(-4);

function monthTitle(c: PCtx, period: string, upper: boolean) {
  const m = +period.slice(5, 7) - 1, y = period.slice(0, 4);
  const en = (MONTHS_LONG_EN[m] || "").toString();
  return (c.s.lang === "ar" ? MONTHS_AR[m] : upper ? en.toUpperCase() : en) + " " + y;
}

export function PayslipBody({ c, e, p, period, compact, gosi, payDay }: { c: PCtx; e: Employee; p: Payslip; period: string; compact: boolean; gosi: GosiSettings | null; payDay: number }) {
  const ref = payslipRef(e, period);
  const payDate = period + "-" + String(payDay).padStart(2, "0");
  const line = (en: string, ar: string, v: number, k: string) => <tr key={k}><td>{c.lb(en, ar)}</td><td className="e num">{n2(v)}</td></tr>;
  const earn: ReactNode[] = [
    line("Basic salary", "الراتب الأساسي", p.basic, "b"),
    line("Housing allowance", "بدل السكن", p.housing, "h"),
    line("Transport allowance", "بدل النقل", p.transport, "t"),
  ];
  if (p.other) earn.push(line("Other allowance", "بدلات أخرى", p.other, "o"));
  if (p.ot) earn.push(line("Overtime " + p.otHours + " h × " + otPct() + "", "عمل إضافي " + p.otHours + " س", p.ot, "ot"));
  if (p.bonus) earn.push(line("Bonus", "مكافأة", p.bonus, "bo"));
  const ded: ReactNode[] = [];
  if (p.gosi.empAnn) ded.push(line("GOSI annuities " + p.gosi.annRate + "%", "المعاشات", p.gosi.empAnn, "ga"));
  if (p.gosi.empSaned) ded.push(line("GOSI SANED" + (gosi ? " " + gosi.saEmpSaned + "%" : ""), "ساند", p.gosi.empSaned, "gs"));
  if (p.abs) ded.push(line("Absence " + p.absDays + " d", "غياب", p.abs, "ab"));
  if (p.loan) ded.push(line("Loan instalment", "قسط قرض", p.loan, "ln"));
  if (p.ded) ded.push(line("Other deductions", "استقطاعات أخرى", p.ded, "dd"));
  if (!p.deductions) ded.push(<tr key="none"><td className="muted2">{c.lb("No deductions", "لا توجد استقطاعات")}</td><td className="e num">0.00</td></tr>);
  const info: [ReactNode, string][] = [
    [c.lb("Employee", "الموظف"), empName(c, e)],
    [c.lb("Employee ID", "الرقم الوظيفي"), e.code],
    [c.lb(e.nationality === "SA" ? "National ID" : "Iqama", e.nationality === "SA" ? "الهوية" : "الإقامة"), e.idNumber],
    [c.lb("Job title", "المسمى"), c.title(e)],
    [c.lb("Department", "القسم"), c.dept(e.dept)],
    [c.lb("Bank / IBAN", "البنك / الآيبان"), e.bank + " · " + maskIban(e.iban)],
  ];
  if (!compact) info.push([c.lb("Pay date", "تاريخ الصرف"), c.gd(payDate) + " · " + hDate(payDate)], [c.lb("Working days", "أيام العمل"), String(30 - p.absDays)]);
  return (
    <>
      <div className="ps-h"><div><div className="ps-k">{c.lb("PAYSLIP", "قسيمة راتب")}</div><div className="ps-m num">{monthTitle(c, period, true)}</div></div><div className="ps-ref num">{ref}</div></div>
      <div className={"ps-info" + (compact ? " c" : "")}>{info.map((r, i) => <div key={i}><span>{r[0]}</span><b>{r[1]}</b></div>)}</div>
      <div className="ps-cols">
        <table className="pt"><thead><tr><th>{c.lb("Earnings", "المستحقات")}</th><th className="e">{curCode()}</th></tr></thead><tbody>{earn}</tbody><tfoot><tr><td>{c.lb("Total earnings", "إجمالي المستحقات")}</td><td className="e num">{n2(p.earnings)}</td></tr></tfoot></table>
        <table className="pt"><thead><tr><th>{c.lb("Deductions", "الاستقطاعات")}</th><th className="e">{curCode()}</th></tr></thead><tbody>{ded}</tbody><tfoot><tr><td>{c.lb("Total deductions", "إجمالي الاستقطاعات")}</td><td className="e num">{n2(p.deductions)}</td></tr></tfoot></table>
      </div>
      <div className="ps-net"><div>{c.lb("NET PAY", "صافي الراتب")}<div className="muted2">{c.lb("Paid by bank transfer (WPS)", "حُوّل بنكيًا عبر حماية الأجور")}</div></div><div className="num" data-testid="ps-net">{curCode()} {n2(p.net)}</div></div>
      {!compact && (
        <>
          <div className="ps-er"><span>{c.lb("Employer GOSI (not deducted)", "حصة صاحب العمل في التأمينات")}</span><span className="num">{p.gosi.erAnn ? <>{c.lb1("Annuities ", "المعاشات ")}{n2(p.gosi.erAnn)} · </> : null}{p.gosi.erSaned ? <>SANED {n2(p.gosi.erSaned)} · </> : null}{c.lb1("Hazards ", "الأخطار ")}{n2(p.gosi.erOH)} = <b>{n2(p.gosi.er)}</b></span></div>
          <div className="ps-foot"><VerifyBlock c={c} refNo={ref} /><div className="muted2">{c.lb("Electronically generated. No signature required.", "صادرة إلكترونيًا ولا تحتاج إلى توقيع.")}</div></div>
        </>
      )}
    </>
  );
}

export function PayslipRoll({ c, e, p, period, payDay, paperW }: { c: PCtx; e: Employee; p: Payslip; period: string; payDay: number; paperW: number }) {
  const L2 = (a: ReactNode, b: string, bold?: boolean) => <div className={"rl" + (bold ? " b" : "")}><span>{a}</span><span className="num">{b}</span></div>;
  const payDate = period + "-" + String(payDay).padStart(2, "0");
  return (
    <div className="rc">
      <div className="rc-c"><b>{c.lb1(c.tenant.name.toUpperCase(), c.tenant.nameAr)}</b><div>CR {c.tenant.cr}</div><div className="rc-t">{c.lb1("PAYSLIP", "قسيمة راتب")} · {monthTitle(c, period, false)}</div></div>
      <div className="rc-hr" />
      {L2(c.lb1("Employee", "الموظف"), c.s.lang === "ar" ? e.nameAr : e.nameEn)}{L2("ID", e.code)}{L2(c.lb1("Pay date", "الصرف"), c.gd(payDate))}
      <div className="rc-hr" />
      {L2(c.lb1("Basic", "الأساسي"), n2(p.basic))}{L2(c.lb1("Housing", "السكن"), n2(p.housing))}{L2(c.lb1("Transport", "النقل"), n2(p.transport))}
      {p.other ? L2(c.lb1("Other", "أخرى"), n2(p.other)) : null}{p.ot ? L2(c.lb1("Overtime", "إضافي"), n2(p.ot)) : null}{p.bonus ? L2(c.lb1("Bonus", "مكافأة"), n2(p.bonus)) : null}
      {L2(c.lb1("Earnings", "المستحقات"), n2(p.earnings), true)}
      <div className="rc-hr d" />
      {p.gosi.emp ? L2("GOSI", "-" + n2(p.gosi.emp)) : null}{p.loan ? L2(c.lb1("Loan", "قرض"), "-" + n2(p.loan)) : null}{p.abs ? L2(c.lb1("Absence", "غياب"), "-" + n2(p.abs)) : null}{p.ded ? L2(c.lb1("Other", "أخرى"), "-" + n2(p.ded)) : null}
      {L2(c.lb1("Deductions", "الاستقطاعات"), n2(p.deductions), true)}
      <div className="rc-hr" />
      <div className="rl xl"><span>{c.lb1("NET PAY", "الصافي")}</span><span className="num">{n2(p.net)}</span></div>
      <div className="rc-c muted2">{curCode()} · WPS</div>
      {c.s.qr && <div className="rc-c"><Qr seed={"PS" + e.id + period} size={paperW > 60 ? 24 : 18} /></div>}
      <div className="rc-c muted2">{c.gd(c.today)} · {c.printedBy}</div>
    </div>
  );
}
